import { CSSProperties, Dispatch, ForwardedRef, forwardRef, Fragment, MouseEvent, ReactNode, Ref, SetStateAction, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { DropdownMenuItem, GMetrixButtonDropdown } from 'components/GMetrixButtonDropdown/GMetrixButtonDropdown';
import type { TableDateFilter, TableFilter } from 'components/GMetrixTable/components/toolbar/components';
import { GMetrixTableColumnCell, GMetrixTableColumnProps } from 'components/GMetrixTable/components';
import { GMetrixTableExportTrigger, GMetrixTableToolbar } from 'components/GMetrixTable/components';
import { buildExportFileName, GenerateUUID, IdGenerator, splitPascalCase } from 'utils';
import { Form, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixTableContext } from 'components/GMetrixTable/context';
import { usePageHeader } from 'components/layout/page/header/context';
import { useRegisterHeaderControls } from 'components/layout';
import { useSuperState, useWindowDimensions } from 'hooks';
import { orderBy, SortDescriptor } from 'utils/dataQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { StudentApi } from 'apis/StudentApi';
import type { Report } from 'apis/ReportApi';
import { saveAs } from 'file-saver';
import { Workbook } from 'exceljs';
import Papa from 'papaparse';

import 'components/GMetrixTable/styles/GmetrixTable.scss';

/** Server-paged exports are capped at this many records. */
export const EXPORT_RECORD_LIMIT = 500;

export type GMetrixTableHandle = {
    csvFileName: string;
    excelFileName: string;
    exportCSV: () => void;
    exportData: any[];
    exportExcel: () => Promise<void>;
}

export interface DetailsButtonItems {
    action: (dataItem?: any) => any;
    iconClass?: string;
    text: string;
}

export interface GMetrixTableExportColumn {
    field: string;
    title?: string;
}

/**
 * Values shared with the GMetrixTable toolbar (and other sub-components) via
 * GMetrixTableContext. Column-related members are typed loosely
 * (GMetrixTableColumnProps<any>) so the module-scope context can stay
 * non-generic, matching the AppContext pattern.
 */
export type GMetrixTableContextValue = {
    allowExport: boolean;
    alteredDropdownItems: GMetrixTableColumnProps<any>[];
    csvFileName: string;
    dateFilter?: TableDateFilter;
    exportData: any[];
    exportExcel: () => Promise<void>;
    extraToolBarButtons?: ReactNode;
    filters?: TableFilter[];
    inlineColumnToggle?: boolean;
    preSearch?: boolean;
    preSearchData: () => Promise<void>;
    searchAutoFocus?: boolean;
    searchPlaceholder?: string;
    searchTerm: string;
    setAlteredDropdownItems: Dispatch<SetStateAction<GMetrixTableColumnProps<any>[]>>;
    setSearchTerm?: (searchTerm: string) => void;
    tableTitle?: string | ReactNode;
    toggleColumns: boolean;
    uniqueTableId: string;
};

interface GMetrixTableProps<T extends { Id: number | string }, U = unknown> {
    actionsHeaderLabel?: ReactNode;
    allowExport?: boolean;
    bottomBarTableName?: string;
    className?: string;
    columnData: GMetrixTableColumnProps<T>[];
    dateFilter?: TableDateFilter;
    defaultItemsPerPage?: number;
    detailsButtonItems?: DetailsButtonItems[];
    detailsDropdownItems?: (dataItem: T) => DropdownMenuItem[];
    editCells?: boolean;
    excelExportFileName?: string;
    exportColumns?: GMetrixTableExportColumn[];
    exportContext?: 'Lookup' | 'Report';
    extraToolBarButtons?: ReactNode;
    filters?: TableFilter[];
    initialSkip?: number;
    inlineColumnToggle?: boolean;
    loading?: boolean;
    lockColumns?: boolean;
    multiSelect?: boolean;
    noDataMessage?: ReactNode;
    onDetailsItemClick?: (dataItem: T) => void;

    /**
     * Server-paged tables only: fetches the full (filtered) result set for export,
     * capped at EXPORT_RECORD_LIMIT. Export then shows a confirmation modal about
     * the cap before fetching. Without it, a server-paged export falls back to the
     * rows currently loaded.
     */
    onExportAllData?: () => Promise<T[]>;
    onPreSearchResults?: (results: T[]) => void;
    onSelectedChange?: (selected: T[]) => void;
    onServerPageChange?: (skip: number, take: number) => void;
    onServerSortChange?: (sort: SortDescriptor[]) => void;
    origionalData?: T[];
    preSearch?: boolean;
    resetPageSignal?: unknown;
    rowData: T[];
    searchAutoFocus?: boolean;
    searchPlaceholder?: string;
    searchTerm?: string;
    selectableRows?: boolean;
    selected?: T[];
    serverSidePagination?: boolean;
    serverTotalCount?: number;
    setCurrentSubReport?: Dispatch<SetStateAction<Report | undefined>>;
    setEditableData?: Dispatch<SetStateAction<string[] | undefined>>;
    setSearchTerm?: (searchTerm: string) => void;
    style?: CSSProperties;
    tableName?: string;
    tableTitle?: string | ReactNode;
    toggleColumns?: boolean;
}

const GMetrixTableInner = <T extends { Id: number | string }, U = unknown>(props: GMetrixTableProps<T, U>, forwardedRef: ForwardedRef<GMetrixTableHandle>) => {

    const { actionsHeaderLabel, allowExport = false, bottomBarTableName, className, columnData, dateFilter, defaultItemsPerPage,
        detailsDropdownItems, editCells, excelExportFileName, exportColumns, exportContext, extraToolBarButtons,
        filters, initialSkip, inlineColumnToggle = false, loading = false, lockColumns, multiSelect = true, noDataMessage, onDetailsItemClick, onExportAllData,
        onPreSearchResults, onSelectedChange, onServerPageChange, onServerSortChange, origionalData, preSearch, resetPageSignal,
        rowData, searchAutoFocus, searchPlaceholder, searchTerm, selectableRows = false, selected = [],
        serverSidePagination = false, serverTotalCount, setCurrentSubReport, setEditableData, setSearchTerm, tableName, tableTitle,
        toggleColumns = false } = props;

    const [state, setState] = useSuperState({
        alteredDropdownItems: (columnData ?? []).map(col => ({
            ...col,
            columnsDropdownSelected: col?.columnsDropdownSelected !== false
        })) as GMetrixTableColumnProps<T>[],
        columnWidths: {} as Record<string, number>,
        exportingAll: false,
        hasNestedTables: false,
        loadingGridData: loading,
        page: { skip: initialSkip ?? 0, take: defaultItemsPerPage ? defaultItemsPerPage : 15 },
        pendingExportFormat: '' as '' | 'excel' | 'csv',
        sort: [] as SortDescriptor[],
        tableData: [] as T[],
        tableKey: `tableKey_${bottomBarTableName ?? 'Results'}`
    });

    const { alteredDropdownItems, columnWidths, exportingAll, hasNestedTables, loadingGridData, page, pendingExportFormat, sort, tableData, tableKey } = state;

    const { formatMessage } = useIntl();
    const displayTableName = bottomBarTableName ?? formatMessage({ id: 'results.fc996ea3', defaultMessage: 'Results' });
    const uniqueTableId = useMemo(() => IdGenerator(), []);
    const { width: viewportWidth } = useWindowDimensions();

    const lastCheckedIndexRef = useRef<number | null>(null);
    const resizingRef = useRef<{ field: string; startWidth: number; startX: number } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const setAlteredDropdownItems: Dispatch<SetStateAction<GMetrixTableColumnProps<T>[]>> = value => setState(prev => ({
        alteredDropdownItems: typeof value === 'function'
            ? (value as (p: GMetrixTableColumnProps<T>[]) => GMetrixTableColumnProps<T>[])(prev.alteredDropdownItems)
            : value
    }));

    const setTableKey: Dispatch<SetStateAction<string>> = value => setState(prev => ({
        tableKey: typeof value === 'function' ? (value as (p: string) => string)(prev.tableKey) : value
    }));

    const fallbackContext = typeof window !== 'undefined' && window.location.pathname.toLowerCase().includes('report')
        ? 'Report'
        : 'Lookup';
    const resolvedContext = exportContext ?? fallbackContext;
    const showExport = allowExport;
    const exportEntity =
        excelExportFileName ||
        tableName ||
        (typeof tableTitle === 'string' ? tableTitle : '') ||
        bottomBarTableName ||
        'TableData';
    const csvFileName = buildExportFileName(resolvedContext, exportEntity, 'csv');
    const excelFileName = buildExportFileName(resolvedContext, exportEntity, 'xlsx');

    // Sync sort key changes
    useEffect(() => {

        setTableKey(GenerateUUID());

    }, [sort]);

    useEffect(() => {

        if (rowData) {

            const firstItem = rowData[0] as any;
            const hasDetails = !!(firstItem && firstItem.Details);

            // Give each detail row a unique DetailKey so expanding one row doesn't expand them all.
            setState({
                tableData: hasDetails
                    ? rowData.map((row, index) => ({ ...(row as any), DetailKey: index }))
                    : rowData
            });
            if (hasDetails) {

                setState({ hasNestedTables: true });

            }

        }

    }, [rowData]);

    // Reset page on search. Server mode skips this internal reset — the parent owns
    // paging there and drives resets via resetPageSignal (typically on the DEBOUNCED
    // term), so zeroing skip on every keystroke would desync it from the parent.
    useEffect(() => {

        if (serverSidePagination) return;

        if (page.skip !== 0) {

            setState(prev => ({ page: { ...prev.page, skip: 0 } }));

        }

    }, [searchTerm]);

    // Reset page to 1 when external filters change (skipping initial mount)
    const isInitialResetSignalRef = useRef(true);
    useEffect(() => {

        if (isInitialResetSignalRef.current) {

            isInitialResetSignalRef.current = false;
            return;

        }
        setState(prev => ({ page: { ...prev.page, skip: 0 } }));

    }, [resetPageSignal]);

    // Reset page if skip exceeds data length
    useEffect(() => {

        if (!serverSidePagination && rowData && rowData.length > 0 && rowData.length <= page.skip && page.skip !== 0) {

            setState(prev => ({ page: { ...prev.page, skip: 0 } }));

        }

        // Server mode: clamp to the last valid page when the total count shrinks below the
        // current skip (e.g. the last row of the final page was archived), and tell the
        // parent so it refetches the clamped page. Never clamp while data is loading — on a
        // remount the parent's total starts at 0 until its fetch resolves, and clamping
        // against that stale 0 would reset the restored page (wiping the URL params).
        if (serverSidePagination && !loadingGridData && serverTotalCount != null && page.skip >= serverTotalCount && page.skip !== 0) {

            const lastSkip = Math.max(0, (Math.ceil(serverTotalCount / page.take) - 1) * page.take);
            setState(prev => ({ page: { ...prev.page, skip: lastSkip } }));
            onServerPageChange?.(lastSkip, page.take);

        }

    }, [rowData, page.skip, serverTotalCount, loadingGridData]);

    // Restore sort from sessionStorage. Pagination (skip/take) is driven by the URL via the
    // initialSkip / defaultItemsPerPage props, so it is intentionally not persisted here.
    useEffect(() => {

        const columnSort = sessionStorage.getItem('columnSort' + tableName);
        if (columnSort) {

            setState({ sort: JSON.parse(columnSort) });

        }

    }, []);

    useEffect(() => {

        setState({ loadingGridData: loading });

    }, [loading]);

    // --- Data editing ---
    function areObjectsEqual(obj1: any, obj2: any): boolean {

        if (obj1 === obj2) return true;
        if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) return false;
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) return false;
        for (const key of keys1) {

            if (!keys2.includes(key) || !areObjectsEqual(obj1[key], obj2[key])) return false;

        }
        return true;

    }

    const grabTableData = (dataRow: T, originalRow: T) => {

        if (origionalData) {

            let data = [...origionalData];
            data = data.map(itemRow => {

                if (areObjectsEqual(itemRow, originalRow)) return dataRow;
                return itemRow;

            });
            if (setEditableData) {

                setEditableData(data as any);
                setState({ tableData: data });

            }

        }

    };

    // --- Pre-search (students) ---
    const preSearchData = async () => {

        if (searchTerm != '') {

            setState({ loadingGridData: true });
            setState({ tableData: [] });
            try {

                const data = await StudentApi.GetAll(searchTerm);
                setState({ tableData: data as any });
                onPreSearchResults?.(data as any);

            } catch (e) {

                console.error(e);

            } finally {

                setState({ loadingGridData: false });

            }

        }

    };

    // --- Selection ---
    const onCheckboxSelect = (dataItem: T) => {

        if (selected?.some(item => item.Id === dataItem.Id)) {

            onSelectedChange?.(selected.filter(item => item.Id !== dataItem.Id));

        } else {

            if (multiSelect) {

                onSelectedChange?.([...selected, dataItem]);

            } else {

                onSelectedChange?.([dataItem]);

            }

        }

    };

    const onClickHeaderCheckboxSelect = (checked: boolean) => {

        if (checked) {

            onSelectedChange?.(tableData);

        } else {

            onSelectedChange?.([]);

        }

    };

    // --- Pagination ---
    const onPageChange = (newPage: { skip: number; take: number }) => {

        setState({ page: newPage });
        if (serverSidePagination && onServerPageChange) {

            onServerPageChange(newPage.skip, newPage.take);

        }

    };

    const effectiveTotalCount = serverSidePagination ? (serverTotalCount ?? 0) : tableData.length;
    const totalPages = Math.ceil(effectiveTotalCount / page.take);
    const currentPageNumber = Math.floor(page.skip / page.take) + 1;

    const pageNumbers = useMemo(() => {

        const pages: number[] = [];

        // Show fewer page buttons on smaller screens so the pager doesn't overflow.
        const buttonCount = viewportWidth < 576 ? 3 : viewportWidth < 768 ? 4 : 5;
        let start = Math.max(1, currentPageNumber - Math.floor(buttonCount / 2));
        const end = Math.min(totalPages, start + buttonCount - 1);
        start = Math.max(1, end - buttonCount + 1);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;

    }, [currentPageNumber, totalPages, viewportWidth]);

    // --- Sorting ---
    const handleSortChange = (field: string) => {

        const col = columnData.find(c => String(c.field ?? '') === field);

        const defaultDir = col?.defaultSortDir;

        let newSort: SortDescriptor[];
        const existingSort = sort.find(s => s.field === field);

        if (defaultDir) {

            // Three-state cycle, but starting from the column's requested
            // default direction: unsorted → defaultDir → opposite → unsorted.
            const oppositeDir = defaultDir === 'asc' ? 'desc' : 'asc';
            if (!existingSort) {

                newSort = [{ dir: defaultDir, field }];

            } else if (existingSort.dir === defaultDir) {

                newSort = [{ dir: oppositeDir, field }];

            } else {

                newSort = [];

            }

        } else if (!existingSort) {

            newSort = [{ dir: 'asc', field }];

        } else if (existingSort.dir === 'asc') {

            newSort = [{ dir: 'desc', field }];

        } else {

            newSort = [];

        }

        if (tableName) {

            sessionStorage.setItem('columnSort' + tableName, JSON.stringify(newSort));

        }
        setState({ sort: newSort });

        // Reset to page 1 whenever sort changes
        setState(prev => ({ page: { ...prev.page, skip: 0 } }));
        if (serverSidePagination && onServerSortChange) {

            // Remap each field to its column's sortField (server-side name) if defined
            const remappedSort = newSort.map(s => {

                const col = columnData.find(c => String(c.field ?? '') === s.field);
                return col?.sortField ? { ...s, field: col.sortField } : s;

            });
            onServerSortChange(remappedSort);

        }

    };

    // --- Column resizing ---
    const onResizeMouseDown = useCallback((e: MouseEvent, field: string, currentWidth: number) => {

        e.preventDefault();
        e.stopPropagation();
        resizingRef.current = { field, startWidth: currentWidth, startX: e.clientX };

        const onMouseMove = (moveEvent: MouseEvent) => {

            if (!resizingRef.current) return;
            const diff = moveEvent.clientX - resizingRef.current.startX;
            const newWidth = Math.max(40, resizingRef.current.startWidth + diff);
            setState(prev => ({ columnWidths: { ...prev.columnWidths, [resizingRef.current!.field]: newWidth } }));

        };

        const onMouseUp = () => {

            resizingRef.current = null;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);

            // The browser fires a `click` event on the <th> immediately after
            // mouseup (because mousedown originated inside it). Intercept that
            // click at the capture phase so it never reaches React's onClick
            // handler and does not trigger an unwanted column sort.
            const suppressClick = (clickEvent: MouseEvent) => {

                clickEvent.stopPropagation();
                document.removeEventListener('click', suppressClick, true);

            };
            document.addEventListener('click', suppressClick, true);

        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

    }, []);

    // --- Expandable rows ---
    const expandChange = (detailKey: any) => {

        const copyOfRowData = [...tableData] as any[];
        setState({
            tableData: copyOfRowData.map(row => {

                if ((row as any).DetailKey === detailKey) {

                    (row as any).Expanded = !(row as any).Expanded;

                }
                return row;

            })
        });

    };

    // --- Export ---
    const hasCustomExportColumns = !!exportColumns && exportColumns.length > 0;

    const getPreparedExportData = (data: any[]) => {

        if (!hasCustomExportColumns || !exportColumns) return data;
        return data.map(dataItem => {

            const exportItem: any = {};
            exportColumns.forEach(col => {

                exportItem[col.field] = dataItem[col.field];

            });
            return exportItem;

        });

    };

    const exportData = getPreparedExportData(tableData);

    // Structural hidden columns (and col.hidden) must be excluded from exports too,
    // matching the visible table which drops these dataTypes.
    const HIDDEN_EXPORT_DATA_TYPES = ['Hidden', 'Id', 'ChildId', 'ParentId'];
    const excelExportColumns = hasCustomExportColumns && exportColumns
        ? exportColumns
        : alteredDropdownItems
            .filter(col => !!col.field && !col.hidden && !HIDDEN_EXPORT_DATA_TYPES.includes(String(col.dataType ?? '')))
            .map(col => ({ field: String(col.field), title: col.title ?? String(col.field) }));

    const exportExcel = async (rows?: any[]) => {

        const dataToExport = rows ? getPreparedExportData(rows) : exportData;

        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet('Sheet1');

        // Check for nested/detail rows
        const hasDetailRows = dataToExport.some((item: any) => item.DetailRows && item.DetailRows.length > 0);

        if (hasDetailRows) {

            // Complex nested export
            const headerCols = excelExportColumns.filter(col => col.field !== 'Expanded' && col.field !== 'DetailKey');

            // Parent headers
            const headerRow = worksheet.addRow(headerCols.map(col => col.title ?? splitPascalCase(col.field)));
            headerRow.eachCell(cell => {

                cell.fill = { fgColor: { argb: 'FF6C6C6C' }, pattern: 'solid', type: 'pattern' };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };

            });

            dataToExport.forEach((dataItem: any) => {

                if (dataItem.DetailRows && dataItem.DetailRows.length) {

                    // Parent row
                    const parentValues = headerCols.map(col => {

                        const val = dataItem[col.field];
                        return (typeof val !== 'object' || val === null) ? val : '';

                    });
                    worksheet.addRow(parentValues);

                    // Nested headers
                    if (dataItem.Details?.Columns) {

                        const nestedHeaderRow = worksheet.addRow(
                            dataItem.Details.Columns.map((c: any) => splitPascalCase(c.field))
                        );
                        nestedHeaderRow.eachCell(cell => {

                            cell.fill = { fgColor: { argb: 'FFC4C4C4' }, pattern: 'solid', type: 'pattern' };
                            cell.font = { bold: true };

                        });

                        // Nested data rows
                        dataItem.DetailRows.forEach((item: any) => {

                            const nestedValues = dataItem.Details.Columns.map((col: any) => {

                                const val = item[col.field];
                                if (typeof val !== 'object') return val;
                                if (col.dataType === 'Action' || col.dataType === 'ProgressBar') return val?.Title;
                                return '';

                            });
                            worksheet.addRow(nestedValues);

                        });

                        // Blank separator row
                        worksheet.addRow([]);

                    }

                }

            });

        } else {

            // Simple flat export
            const headerRow = worksheet.addRow(excelExportColumns.map(col => col.title ?? col.field));
            headerRow.eachCell(cell => {

                cell.fill = { fgColor: { argb: 'FF6C6C6C' }, pattern: 'solid', type: 'pattern' };
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };

            });

            dataToExport.forEach((dataItem: any) => {

                const values = excelExportColumns.map(col => {

                    const val = dataItem[col.field];
                    if (val === null || val === undefined) return '';
                    if (typeof val === 'object') return val.Title ?? '';
                    return val;

                });
                worksheet.addRow(values);

            });

        }

        // Auto-size columns
        worksheet.columns.forEach(column => {

            let maxLength = 10;
            column.eachCell?.({ includeEmpty: true }, cell => {

                const cellLength = cell.value ? String(cell.value).length : 0;
                if (cellLength > maxLength) maxLength = cellLength;

            });
            column.width = Math.min(maxLength + 2, 50);

        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), excelFileName);

    };

    const exportCSV = (rows?: any[]) => {

        const dataToExport = rows ? getPreparedExportData(rows) : exportData;

        const csvRows = dataToExport.map((dataItem: any) => {

            const row: Record<string, any> = {};
            excelExportColumns.forEach(col => {

                const val = dataItem[col.field];
                row[col.title ?? col.field] = val === null || val === undefined
                    ? ''
                    : typeof val === 'object'
                        ? val.Title ?? ''
                        : val;

            });
            return row;

        });

        const csv = Papa.unparse(csvRows);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), csvFileName);

    };

    // Server-paged tables confirm the record cap first — but only when the result
    // set actually exceeds the cap; smaller sets export silently. Client-side
    // tables always export directly.
    const requestExport = (format: 'excel' | 'csv') => {

        if (serverSidePagination) {

            if (effectiveTotalCount > EXPORT_RECORD_LIMIT) {

                setState({ pendingExportFormat: format });
                return;

            }

            void runLimitedExport(format);
            return;

        }

        format === 'excel' ? void exportExcel() : exportCSV();

    };

    const runLimitedExport = async (format: 'excel' | 'csv' | '' = pendingExportFormat) => {

        if (!format) return;

        try {

            setState({ exportingAll: true });

            // Fetch the full filtered set when the caller provides it; otherwise fall
            // back to the rows already loaded. Either way, cap at the export limit.
            const rows = (onExportAllData ? await onExportAllData() : tableData).slice(0, EXPORT_RECORD_LIMIT);

            format === 'excel' ? await exportExcel(rows) : exportCSV(rows);
            setState({ exportingAll: false, pendingExportFormat: '' });

        } catch {

            setState({ exportingAll: false, pendingExportFormat: '' });

        }

    };

    // --- Visible columns ---
    // Derive from the fresh columnData prop so that cell renderers (which may
    // close over parent state) are never stale.  Toggle-visibility state is
    // read from alteredDropdownItems, which is the only piece that can diverge
    // from the original columnData (via the ToggleColumns UI).
    const isColumnNullForAllRows = (field?: string): boolean => {

        // With server-side pagination only the current page is held in memory, so a
        // page that happens to be all-null for a field (e.g. when sorting brings the
        // rows with no value to the top) is NOT evidence that the column is globally
        // empty. Auto-hiding here would make the column the user just sorted by
        // disappear, so this heuristic only applies to fully client-side data.
        if (serverSidePagination) return false;

        const rows = tableData.length > 0 ? tableData : (rowData as T[] | undefined);
        if (!field || !rows || rows.length === 0) return false;
        return rows.every(row => {

            const value = field.split('.').reduce((acc: any, k) => acc?.[k], row);
            return value === null || value === undefined;

        });

    };

    const visibleColumns = (columnData ?? []).filter(col => {

        const key = String(col.field ?? col.title ?? '');
        const toggleItem = alteredDropdownItems?.find(a => String(a.field ?? a.title ?? '') === key);
        const isToggled = toggleItem?.columnsDropdownSelected ?? col.columnsDropdownSelected !== false;

        switch (col.dataType) {

            case 'Hidden':
            case 'Id':
            case 'ChildId':
            case 'ParentId':
                return false;
            default:
                if (col.hidden || !isToggled) return false;

                if (isColumnNullForAllRows(col.field)) return false;
                return true;

        }

    });

    // --- Sorted + paginated data ---
    const sortedData = serverSidePagination ? tableData : orderBy(tableData, sort);
    const paginatedData = serverSidePagination
        ? tableData.map(row => ({
            ...row,
            selected: selected.some(selectedRow => row.Id === selectedRow.Id)
        }))
        : sortedData.slice(page.skip, page.take + page.skip).map(row => ({
            ...row,
            selected: selected.some(selectedRow => row.Id === selectedRow.Id)
        }));

    const onCheckboxClick = (e: MouseEvent, dataItem: T, sortedIndex: number) => {

        if (e.shiftKey && multiSelect && lastCheckedIndexRef.current !== null) {

            // Clear any text selection caused by shift-click
            window.getSelection()?.removeAllRanges();
            const start = Math.min(lastCheckedIndexRef.current, sortedIndex);
            const end = Math.max(lastCheckedIndexRef.current, sortedIndex);
            const rangeItems = sortedData.slice(start, end + 1);
            const newSelected = [...selected];
            rangeItems.forEach(item => {

                if (!newSelected.some(s => s.Id === item.Id)) {

                    newSelected.push(item);

                }

            });
            onSelectedChange?.(newSelected);

        } else {

            onCheckboxSelect(dataItem);

        }
        lastCheckedIndexRef.current = sortedIndex;

    };

    const onRowClick = (e: MouseEvent, dataItem: T, sortedIndex: number) => {

        // Don't toggle selection when clicking on interactive elements within the row
        // (buttons, links, form controls, etc.). The checkbox itself bubbles up here
        // because its cell has no onClick handler.
        const target = e.target as HTMLElement;
        if (target.closest('button, a, select, textarea, [role="button"]')) {

            return;

        }

        // Prevent the browser from toggling the native checkbox (we manage it in state)
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'checkbox') {

            e.preventDefault();

        }
        onCheckboxClick(e, dataItem, sortedIndex);

    };

    const onRowMouseDown = (e: MouseEvent) => {

        // Prevent text selection range when shift-clicking rows for range select
        if (e.shiftKey) {

            e.preventDefault();

        }

    };

    // Navigate to the row's detail page when the row is clicked. Clicks that
    // originate from interactive controls (buttons, links, form inputs) or the
    // sticky action/selection columns are ignored so they keep their own behaviour.
    const onRowDetailsClick = (e: MouseEvent, dataItem: T) => {

        if ((dataItem as any).DisabledDetailsBtn) {

            return;

        }

        const target = e.target as HTMLElement;
        if (target.closest('button, a, select, textarea, label, input, [role="button"], .gmetrix-table__td--sticky')) {

            return;

        }

        onDetailsItemClick?.(dataItem);

    };

    // Server mode always shows the pager (even with 0 or < 10 rows) so the range
    // text and page-size control stay visible; client mode keeps the >= 10 gate.
    const showPagination = serverSidePagination ? true : tableData && tableData.length >= 10;
    const totalColSpan = visibleColumns.length + (selectableRows ? 1 : 0) + (onDetailsItemClick ? 1 : 0) + (hasNestedTables ? 1 : 0);

    // --- Column width helpers ---
    const getColumnWidth = (col: GMetrixTableColumnProps<T>) => {

        const field = String(col.field ?? col.title ?? '');
        if (columnWidths[field]) return columnWidths[field];
        if (col.width) return typeof col.width === 'number' ? col.width : parseInt(col.width as string, 10);
        const colCount = visibleColumns.length;
        if (colCount >= 10) return 150;
        if (typeof window !== 'undefined' && window.innerWidth > 1000) return undefined;
        return 170;

    };

    const shouldLockColumn = (col: GMetrixTableColumnProps<T>) => {

        if (col.locked) return true;
        if (window.location.href.includes('/reports') || lockColumns) {

            if (col.field === 'Name' || col.title === 'Name') return true;

        }
        return false;

    };

    const context: GMetrixTableContextValue = {
        allowExport,
        alteredDropdownItems,
        csvFileName,
        dateFilter,
        exportData,
        exportExcel,
        extraToolBarButtons,
        filters,
        inlineColumnToggle,
        preSearch,
        preSearchData,
        searchAutoFocus,
        searchPlaceholder,
        searchTerm: searchTerm ?? '',
        setAlteredDropdownItems,
        setSearchTerm,
        tableTitle,
        toggleColumns,
        uniqueTableId
    };

    const pageHeader = usePageHeader();

    // Keep the freshest export closures in a ref so a single, stable handle can
    // be registered once (avoiding re-register loops) while still exporting
    // current data at click time. Handles route through requestExport so
    // server-paged tables get the record-cap confirmation first.
    const exportHandleRef = useRef<GMetrixTableHandle>({ csvFileName, excelFileName, exportCSV: () => requestExport('csv'), exportData, exportExcel: async () => requestExport('excel') });
    exportHandleRef.current = { csvFileName, excelFileName, exportCSV: () => requestExport('csv'), exportData, exportExcel: async () => requestExport('excel') };

    const stableExportHandleRef = useRef<GMetrixTableHandle | null>(null);
    if (stableExportHandleRef.current === null) {

        stableExportHandleRef.current = {
            get csvFileName() {

                return exportHandleRef.current.csvFileName;

            },
            get excelFileName() {

                return exportHandleRef.current.excelFileName;

            },
            exportCSV: () => exportHandleRef.current.exportCSV(),
            get exportData() {

                return exportHandleRef.current.exportData;

            },
            exportExcel: () => exportHandleRef.current.exportExcel()
        };

    }

    // When rendered inside a PageWithHeader, dial the export control out to the
    // header. Header-less pages fall back to the inline trigger rendered below.
    // (Tab panes that host this table unmount when inactive — see GMetrixTabs
    // unmountOnExit — so only the visible table ever registers.)
    useRegisterHeaderControls(showExport ? { exportHandle: stableExportHandleRef.current } : null);

    useImperativeHandle(forwardedRef, () => ({
        csvFileName,
        excelFileName,
        exportCSV: () => requestExport('csv'),
        exportData,
        exportExcel: async () => requestExport('excel')
    } as GMetrixTableHandle));

    const showToolbar = allowExport
        || toggleColumns
        || setSearchTerm
        || extraToolBarButtons
        || filters?.length
        || dateFilter
        || tableTitle;

    return (
        <GMetrixTableContext.Provider value={context}>
            <div
                className={`gmetrix-table d-flex flex-column gap-3 ${className ?? ''}`}
                id={uniqueTableId}
            >

                {showToolbar && <GMetrixTableToolbar />}

                {showExport && !pageHeader && (
                    <div className="d-flex justify-content-end">
                        <GMetrixTableExportTrigger handle={stableExportHandleRef.current as GMetrixTableHandle} type="button" />
                    </div>
                )}

                {/* Record-cap confirmation for server-paged exports */}
                <Modal centered onHide={() => !exportingAll && setState({ pendingExportFormat: '' })} show={!!pendingExportFormat}>
                    <Modal.Header closeButton={!exportingAll}>
                        <Modal.Title as={'h5'}>
                            {formatMessage({ id: 'export_limit.42212b6e', defaultMessage: 'Export limit' })}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {formatMessage(
                            { id: 'this_export_will_be_limited_to_the_first.87fd7206', defaultMessage: 'This export will be limited to the first {limit} records of the current results. Do you want to continue?' },
                            { limit: EXPORT_RECORD_LIMIT }
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <GMetrixButton disabled={exportingAll} onClick={() => setState({ pendingExportFormat: '' })} variant={'outline-secondary'}>
                            {formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })}
                        </GMetrixButton>
                        <GMetrixButton disabled={exportingAll} onClick={() => void runLimitedExport()} variant={'primary'}>
                            {exportingAll
                                ? formatMessage({ id: 'exporting.fd3ba5c9', defaultMessage: 'Exporting…' })
                                : formatMessage({ id: 'export.6f7a465c', defaultMessage: 'Export' })}
                        </GMetrixButton>
                    </Modal.Footer>
                </Modal>

                <div>
                    {/* Table */}
                    <div
                        className="gmetrix-table__container"
                        ref={containerRef}
                    >

                        {loadingGridData && paginatedData.length > 0 && (
                            <div className="gmetrix-table__overlay">
                                <GMetrixLoader />
                            </div>
                        )}

                        {/* `responsive` adds its own scroll wrapper, which would stop the header from sticking. */}
                        <Table bordered hover className="gmetrix-table__table mb-0" key={tableKey}>

                            <thead className="gmetrix-table__header">
                                <tr>

                                    {/* Expand column */}
                                    {hasNestedTables && (
                                        <th className="gmetrix-table__th--sticky" style={{ width: '40px' }}></th>
                                    )}

                                    {/* Selection column */}
                                    {selectableRows && (
                                        <th className="gmetrix-table__th--sticky gmetrix-table__th--checkbox" style={{ width: '42px' }}>
                                            {multiSelect && (
                                                <Form.Check
                                                    checked={tableData.length > 0 && tableData.length === selected.length}
                                                    onChange={e => onClickHeaderCheckboxSelect(e.target.checked)}
                                                    type="checkbox"
                                                />
                                            )}
                                        </th>
                                    )}

                                    {/* Data columns */}
                                    {visibleColumns.map((col, key) => {

                                        const field = String(col.field ?? col.title ?? '');
                                        const colWidth = getColumnWidth(col);
                                        const isLocked = shouldLockColumn(col);
                                        const sortDescriptor = sort.find(s => s.field === field);
                                        const displayTitle = col.title ??
                                            (col.field ? splitPascalCase(col.field as string) : '');
                                        const isTruncated = typeof displayTitle === 'string' && displayTitle.length > 40;
                                        const truncatedTitle = isTruncated ? `${(displayTitle as string).slice(0, 40)}…` : displayTitle;

                                        if (col.headerCell) {

                                            const HeaderCell = col.headerCell;
                                            return (
                                                <th
                                                    className={`gmetrix-table__th ${isLocked ? 'gmetrix-table__th--sticky' : ''}`}
                                                    key={key}
                                                    style={{ width: colWidth ? `${colWidth}px` : undefined }}
                                                >
                                                    <HeaderCell field={field} title={displayTitle} />
                                                </th>
                                            );

                                        }

                                        const isSortable = col.sortable !== false;

                                        return (
                                            <th
                                                style={{
                                                    cursor: isSortable ? 'pointer' : 'default',
                                                    userSelect: 'none',
                                                    width: colWidth ? `${colWidth}px` : undefined
                                                }}
                                                className={`gmetrix-table__th ${isLocked ? 'gmetrix-table__th--sticky' : ''} ${sortDescriptor ? 'gmetrix-table__th--sorted' : ''}`}
                                                key={field}
                                                onClick={isSortable ? () => handleSortChange(field) : undefined}
                                            >
                                                {isTruncated ?
                                                    (
                                                        <OverlayTrigger
                                                            overlay={(
                                                                <Tooltip id={`gmetrix-table-header-tooltip-${field}`}>
                                                                    {displayTitle}
                                                                </Tooltip>
                                                            )}
                                                            container={typeof document !== 'undefined' ? document.body : undefined}
                                                            placement="top"
                                                            trigger={['hover', 'focus']}
                                                        >
                                                            <span className="gmetrix-table__th-content">

                                                                {truncatedTitle}

                                                                {sortDescriptor && (
                                                                <i className={`ms-1 fa-solid ${sortDescriptor.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`}/>
                                                                )}

                                                            </span>
                                                        </OverlayTrigger>
                                                    )
                                                    :
                                                    (
                                                        <span className="gmetrix-table__th-content">

                                                            {truncatedTitle}

                                                            {sortDescriptor && (
                                                            <i className={`ms-1 fa-solid ${sortDescriptor.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`}/>
                                                            )}

                                                        </span>
                                                    )
                                                }

                                                {/* Resize handle */}
                                                <span
                                                    className="gmetrix-table__resize-handle"
                                                    onMouseDown={e => onResizeMouseDown(e, field, colWidth ?? 150)}
                                                />

                                            </th>
                                        );

                                    })}

                                    {/* Details column */}
                                    {onDetailsItemClick && (
                                        <th className="gmetrix-table__th--sticky" style={{ width: detailsDropdownItems ? '150px' : '98px' }}>
                                            {actionsHeaderLabel !== undefined
                                                ? actionsHeaderLabel
                                                : <FormattedMessage defaultMessage="Actions" id="actions.61fd1d29" />}
                                        </th>
                                    )}

                                </tr>
                            </thead>

                            <tbody>
                                {paginatedData.length === 0 ?
                                    (
                                        <tr>
                                            <td className="text-center py-4" colSpan={totalColSpan}>
                                                {loadingGridData ?
                                                    (
                                                        <GMetrixLoader />
                                                    )
                                                    : !noDataMessage ?
                                                        <FormattedMessage defaultMessage="No Records Match Your Search" id="no_records_match_your_search.2babbffd" />
                                                        :
                                                        (
                                                            noDataMessage
                                                        )
                                                }
                                            </td>
                                        </tr>
                                    )
                                    :
                                    (
                                        paginatedData.map((row, key) => {

                                            const dataItem = row as any;
                                            const isExpanded = dataItem.Expanded;
                                            return (
                                                <Fragment key={key}>
                                                    <tr
                                                        onClick={selectableRows
                                                            ? e => onRowClick(e, row, page.skip + key)
                                                            : onDetailsItemClick
                                                                ? e => onRowDetailsClick(e, row)
                                                                : undefined}
                                                        className={dataItem.selected ? 'table-primary' : ''}
                                                        onMouseDown={selectableRows ? onRowMouseDown : undefined}
                                                        style={selectableRows || onDetailsItemClick ? { cursor: 'pointer' } : undefined}
                                                    >

                                                        {/* Expand button */}
                                                        {hasNestedTables && (
                                                            <td className="text-center gmetrix-table__td--sticky" style={{ padding: '5px', width: '40px' }}>
                                                                {dataItem.Details && (
                                                                    <GMetrixButton
                                                                        onClick={() => expandChange(dataItem.DetailKey)}
                                                                        size="sm"
                                                                        variant="outline-secondary"
                                                                    >
                                                                        <i className={`fa-solid ${isExpanded ? 'fa-minus' : 'fa-plus'}`}></i>
                                                                    </GMetrixButton>
                                                                )}
                                                            </td>
                                                        )}

                                                        {/* Selection checkbox */}
                                                        {selectableRows && (
                                                            <td
                                                                onClick={e => {

                                                                    e.stopPropagation();
                                                                    onCheckboxClick(e, row, page.skip + key);

                                                                }}
                                                                className="text-center gmetrix-table__td--sticky"
                                                                style={{ width: '42px' }}
                                                            >
                                                                <Form.Check
                                                                    checked={!!dataItem.selected}
                                                                    onChange={() => undefined}
                                                                    type={multiSelect ? 'checkbox' : 'radio'}
                                                                />
                                                            </td>
                                                        )}

                                                        {/* Data cells */}
                                                        {visibleColumns.map((col, key) => (
                                                            <GMetrixTableColumnCell
                                                                key={key}
                                                                {...col}
                                                                columnNumber={visibleColumns.length}
                                                                dataItem={row}
                                                                editCells={editCells}
                                                                grabTableData={grabTableData}
                                                                lockColumns={lockColumns}
                                                                setCurrentSubReport={setCurrentSubReport}
                                                                setTableKey={setTableKey}
                                                                tableData={tableData}
                                                            />
                                                        ))}

                                                        {/* Details button */}
                                                        {onDetailsItemClick && (
                                                            <td className="gmetrix-table__td--sticky" style={{ padding: '5px', width: detailsDropdownItems ? '150px' : '98px' }}>
                                                                {detailsDropdownItems ?
                                                                    (
                                                                        <GMetrixButtonDropdown
                                                                            connectedButton={(
                                                                                <GMetrixButton
                                                                                    className="joyride-details"
                                                                                    disabled={dataItem.DisabledDetailsBtn}
                                                                                    onClick={() => onDetailsItemClick(dataItem)}
                                                                                    variant="outline-primary"
                                                                                >
                                                                                    {<FormattedMessage defaultMessage="View" id="view.1590505b" />}
                                                                                </GMetrixButton>
                                                                            )}
                                                                            className="joyride-details"
                                                                            dataItems={detailsDropdownItems(dataItem)}
                                                                            iconClass="fa-solid fa-ellipsis-vertical"
                                                                            variant="outline-primary"
                                                                        />
                                                                    )
                                                                    :
                                                                    (
                                                                        <GMetrixButton
                                                                            className="joyride-details"
                                                                            disabled={dataItem.DisabledDetailsBtn}
                                                                            onClick={() => onDetailsItemClick(dataItem)}
                                                                            variant="outline-primary"
                                                                        >
                                                                            {<FormattedMessage defaultMessage="View" id="view.1590505b" />}
                                                                        </GMetrixButton>
                                                                    )}
                                                            </td>
                                                        )}
                                                    </tr>

                                                    {/* Expanded detail/nested table */}
                                                    {hasNestedTables && isExpanded && dataItem.Details && (
                                                        <tr className="gmetrix-table__detail-row">
                                                            <td colSpan={totalColSpan} style={{ padding: '0.5rem 1rem' }}>
                                                                <GMetrixTable
                                                                    columnData={dataItem.Details.Columns ?? []}
                                                                    rowData={dataItem.Details.Data ?? []}
                                                                    setCurrentSubReport={setCurrentSubReport}
                                                                />
                                                            </td>
                                                        </tr>
                                                    )}

                                                </Fragment>
                                            );

                                        })

                                    )}
                            </tbody>

                        </Table>
                    </div>

                    {/* Pagination */}
                    {showPagination && (
                        <div className="gmetrix-table__pager d-flex flex-wrap justify-content-between align-items-center gap-2 border rounded-bottom px-3 py-2 bg-light">

                            <div className="d-flex flex-wrap align-items-center gap-2">

                                <GMetrixButton
                                    disabled={page.skip === 0}
                                    onClick={() => onPageChange({ skip: 0, take: page.take })}
                                    size="sm"
                                    variant="outline-secondary"
                                >
                                    <i className="fa-solid fa-angles-left"></i>
                                </GMetrixButton>

                                <GMetrixButton
                                    disabled={page.skip === 0}
                                    onClick={() => onPageChange({ skip: Math.max(0, page.skip - page.take), take: page.take })}
                                    size="sm"
                                    variant="outline-secondary"
                                >
                                    <i className="fa-solid fa-angle-left"></i>
                                </GMetrixButton>

                                {pageNumbers.map(num => (
                                    <GMetrixButton
                                        key={num}
                                        onClick={() => onPageChange({ skip: (num - 1) * page.take, take: page.take })}
                                        size="sm"
                                        variant={num === currentPageNumber ? 'primary' : 'outline-secondary'}
                                    >
                                        {num}
                                    </GMetrixButton>
                                ))}

                                <GMetrixButton
                                    disabled={page.skip + page.take >= effectiveTotalCount}
                                    onClick={() => onPageChange({ skip: page.skip + page.take, take: page.take })}
                                    size="sm"
                                    variant="outline-secondary"
                                >
                                    <i className="fa-solid fa-angle-right"></i>
                                </GMetrixButton>

                                <GMetrixButton
                                    disabled={page.skip + page.take >= effectiveTotalCount}
                                    onClick={() => onPageChange({ skip: (totalPages - 1) * page.take, take: page.take })}
                                    size="sm"
                                    variant="outline-secondary"
                                >
                                    <i className="fa-solid fa-angles-right"></i>
                                </GMetrixButton>

                            </div>

                            <div className="d-flex align-items-center gap-3">
                                <span className="text-muted">
                                    <b>
                                        {serverSidePagination ?
                                            (
                                                effectiveTotalCount === 0
                                                    ? <FormattedMessage defaultMessage="0 of 0 items" id="0_of_0_items.ebaf36ca" />
                                                    : (
                                                        <FormattedMessage
                                                            values={{
                                                                end: String(Math.min(page.skip + page.take, effectiveTotalCount)),
                                                                start: String(Math.min(page.skip + 1, effectiveTotalCount)),
                                                                total: String(effectiveTotalCount)
                                                            }}
                                                            defaultMessage="{start}–{end} of {total} items" id="startend_of_total_items.bdf8af71"
                                                        />
                                                    )
                                            )
                                            :
                                            (
                                                <>
                                                    {effectiveTotalCount}
                                                    {' '}
                                                    {displayTableName}
                                                </>
                                            )}
                                    </b>
                                </span>
                                <div className="d-flex align-items-center gap-2">
                                    <Form.Select
                                        className="w-auto"
                                        onChange={e => onPageChange({ skip: 0, take: Number(e.target.value) })}
                                        size="sm"
                                        value={page.take}
                                    >
                                        {[10, 15, 25, 50, 100, 250, 500].map(size => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    {serverSidePagination && (
                                        <span className="text-muted text-nowrap">
                                            <FormattedMessage defaultMessage="items per page" id="items_per_page.6a53cd98" />
                                        </span>
                                    )}
                                </div>
                            </div>

                        </div>
                    )}
                </div>

            </div>
        </GMetrixTableContext.Provider>
    );

};

export const GMetrixTable = forwardRef(GMetrixTableInner) as <T extends { Id: number | string }, U = unknown>(
    props: GMetrixTableProps<T, U> & { ref?: Ref<GMetrixTableHandle> }
) => ReturnType<typeof GMetrixTableInner>;